import React from 'react';
import { useSiteSettings } from '../context/SiteSettingsContext';

function PrivacyPolicy() {
  const { settings } = useSiteSettings();
  const churchName = settings.church_name || 'Coos Bay Cornerstone';
  const churchEmail = settings.email || 'info@coosbaycornerstone.com';
  const churchPhone = settings.phone || '(541) 808-9393';
  const churchAddress = `${settings.address || '63748 Harriet Road'}, ${settings.city_state_zip || 'Coos Bay, Oregon 97420'}`;

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="card p-8 md:p-12">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
            Privacy Policy
          </h1>
          <p className="text-slate-500 mb-8">Last updated: February 2025</p>

          <div className="prose prose-lg max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Introduction</h2>
              <p className="text-slate-700 leading-relaxed">
                {churchName} ("we," "our," or "us") is committed to protecting the privacy of our members, visitors, 
                and website users. This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                information when you visit our website or interact with our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Information We Collect</h2>
              <p className="text-slate-700 leading-relaxed mb-4">We may collect the following types of information:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>Personal Information:</strong> Name, email address, phone number, and mailing address when you register, submit a prayer request, or contact us.</li>
                <li><strong>Communication Preferences:</strong> Your preferences for receiving notifications about events, announcements, and prayer updates via email or text message.</li>
                <li><strong>Donation Information:</strong> When you make a donation, we may collect payment information necessary to process your gift. We do not store complete credit card information on our servers.</li>
                <li><strong>Usage Data:</strong> Information about how you interact with our website, including pages visited and time spent on the site.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>How We Use Your Information</h2>
              <p className="text-slate-700 leading-relaxed mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Communicate with you about church events, services, and announcements</li>
                <li>Send prayer request updates and spiritual encouragement</li>
                <li>Process donations and provide tax receipts</li>
                <li>Respond to your inquiries and prayer requests</li>
                <li>Send SMS notifications if you have opted in to receive text messages</li>
                <li>Improve our website and services</li>
                <li>Maintain church membership records</li>
              </ul>
            </section>

            <section id="sms-terms">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>SMS/Text Message Terms and Conditions</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                By opting in to receive SMS notifications from {churchName}, you agree to the following terms:
              </p>
              
              <div className="bg-blue-50 p-6 rounded-lg mb-4">
                <h3 className="font-bold text-blue-900 mb-3">Opt-In Consent</h3>
                <p className="text-slate-700 leading-relaxed">
                  By checking the "Text Message" checkbox during registration or in your account settings, you expressly consent to receive text messages from {churchName} at the mobile phone number you provided. Your consent is not required as a condition of membership or any purchase.
                </p>
              </div>

              <div className="bg-amber-50 p-6 rounded-lg mb-4">
                <h3 className="font-bold text-amber-900 mb-3">Message Types</h3>
                <p className="text-slate-700 leading-relaxed mb-2">You may receive text messages regarding:</p>
                <ul className="list-disc pl-6 space-y-1 text-slate-700">
                  <li>Church event reminders and announcements</li>
                  <li>Service time changes or cancellations</li>
                  <li>Prayer chain notifications</li>
                  <li>Emergency communications</li>
                  <li>Ministry group updates</li>
                  <li>Community outreach information</li>
                </ul>
              </div>

              <div className="bg-gray-100 p-6 rounded-lg mb-4">
                <h3 className="font-bold text-gray-900 mb-3">Important Information</h3>
                <ul className="space-y-2 text-slate-700">
                  <li><strong>Message Frequency:</strong> Message frequency varies based on church activities and your notification preferences.</li>
                  <li><strong>Message and Data Rates:</strong> Message and data rates may apply. Please check with your mobile carrier for details about your text messaging plan.</li>
                  <li><strong>Opt-Out:</strong> Reply <strong>STOP</strong> to any message to opt out of receiving text messages. You will receive a confirmation message.</li>
                  <li><strong>Help:</strong> Reply <strong>HELP</strong> to any message for assistance or contact us at {churchPhone}.</li>
                  <li><strong>Supported Carriers:</strong> Major US carriers are supported. T-Mobile is not liable for delayed or undelivered messages.</li>
                </ul>
              </div>

              <p className="text-slate-700 leading-relaxed">
                We respect your privacy and will never share your phone number with third parties for marketing purposes. 
                For more information about how we protect your data, see the sections above.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Information Sharing</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>With Your Consent:</strong> When you give us permission to share your information</li>
                <li><strong>Service Providers:</strong> With trusted third-party services that help us operate our website and communications (such as our SMS provider)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Prayer Requests:</strong> Shared prayer requests may be distributed to our prayer team unless you request confidentiality</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Data Security</h2>
              <p className="text-slate-700 leading-relaxed">
                We implement appropriate security measures to protect your personal information against unauthorized access, 
                alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, 
                and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Your Rights</h2>
              <p className="text-slate-700 leading-relaxed mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Opt out of marketing communications</li>
                <li>Unsubscribe from SMS messages at any time by texting STOP</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Children's Privacy</h2>
              <p className="text-slate-700 leading-relaxed">
                We do not knowingly collect personal information from children under 13 without parental consent. 
                If you believe we have collected information from a child under 13, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Changes to This Policy</h2>
              <p className="text-slate-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
                the new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Contact Us</h2>
              <p className="text-slate-700 leading-relaxed">
                If you have questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="mt-4 p-6 bg-slate-50 rounded-lg">
                <p className="text-slate-700 font-semibold">{churchName}</p>
                <p className="text-slate-700">{churchAddress}</p>
                <p className="text-slate-700">Phone: {churchPhone}</p>
                <p className="text-slate-700">Email: {churchEmail}</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
